import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowCounterClockwise, Archive, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLLECTION_LABELS = {
  budget_requests: "Budget Requests",
  quotations: "Quotations",
  purchase_orders: "Purchase Orders",
  billing_requests: "Billing Requests",
  client_invoices: "Invoices",
  liquidations: "Liquidations",
  clients: "Clients",
  suppliers: "Suppliers",
  permits: "Permits",
};

function HardDeleteButton({ collection, item, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const testId = `hard-delete-${collection}-${item.id}`;

  async function doDelete() {
    setLoading(true);
    try {
      await api.delete(`/trash/${collection}/${item.id}`);
      toast.success(`${item.label} permanently deleted`);
      setOpen(false); setConfirmText("");
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setLoading(false); }
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={testId}>
          <Trash size={14} className="mr-1" /> Delete permanently
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete “{item.label}”?</AlertDialogTitle>
          <AlertDialogDescription>
            This wipes the record from the database. It cannot be restored, and any linked exports or audit trails will still reference its ID but the record itself will be gone. This action is admin-only and is logged.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5 py-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type <span className="font-mono text-rose-700">DELETE</span> to confirm</Label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            className="border-border font-mono"
            data-testid={`${testId}-confirm-input`}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); doDelete(); }}
            disabled={loading || confirmText !== "DELETE"}
            className="bg-rose-600 hover:bg-rose-700 focus:bg-rose-700 text-white"
            data-testid={`${testId}-confirm`}
          >
            {loading ? "Deleting…" : "Delete permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function ArchivedPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setData(data || {});
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function restore(collection, id) {
    try {
      await api.post(`/trash/${collection}/${id}/restore`);
      toast.success("Restored to active list");
      load();
    } catch { toast.error("Restore failed"); }
  }

  const totalCount = Object.values(data).reduce((s, arr) => s + (arr?.length || 0), 0);
  const collectionKeys = Object.keys(COLLECTION_LABELS);
  const firstNonEmpty = collectionKeys.find((k) => (data[k]?.length || 0) > 0) || "budget_requests";

  return (
    <div className="space-y-6" data-testid="archived-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><Archive size={12} /> Archive</div>
          <h1 className="section-title text-3xl sm:text-4xl">Archived Records</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Records archived from any module. They stay in the database and can be restored to the active list at any time.
            {totalCount === 0 ? " Nothing archived yet." : ` ${totalCount} archived item(s).`}
            {isAdmin && " Admin only: click Delete permanently to wipe a record for good."}
          </p>
        </div>
      </div>

      <Tabs defaultValue={firstNonEmpty}>
        <TabsList className="border border-border flex-wrap h-auto">
          {collectionKeys.map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`archive-tab-${k}`}>
              {COLLECTION_LABELS[k]} ({data[k]?.length || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        {collectionKeys.map((coll) => (
          <TabsContent key={coll} value={coll} className="mt-4">
            <div className="border border-border rounded-md bg-card overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name / Ref</TableHead>
                    <TableHead>Archived</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                  ) : (data[coll] || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">No archived {COLLECTION_LABELS[coll].toLowerCase()}.</TableCell></TableRow>
                  ) : data[coll].map((it) => (
                    <TableRow key={it.id} data-testid={`archived-row-${coll}-${it.id}`}>
                      <TableCell className="font-medium">
                        <div>{it.label}</div>
                        {it.deleted_reason && (
                          <div className="text-xs text-muted-foreground mt-0.5 max-w-md truncate" title={it.deleted_reason}>
                            Reason: {it.deleted_reason}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{(it.deleted_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                      <TableCell className="text-sm">{it.deleted_by_name || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" className="border-border" onClick={() => restore(coll, it.id)} data-testid={`restore-${coll}-${it.id}`}>
                            <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                          </Button>
                          {isAdmin && (
                            <HardDeleteButton collection={coll} item={it} onDeleted={load} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
